'use client'

import { Fuel, Loader2, Plus, X } from 'lucide-react'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { HEIGHT_MAX_M, HEIGHT_MIN_M } from '@/lib/clearance'
import { MAX_WAYPOINTS } from '@/lib/constants'
import { FUEL_PLAN_ENABLED } from '@/lib/flags'
import { planFuelStops } from '@/lib/fuel-stops'
import { storageKeys } from '@/lib/storage-keys'
import type { VehicleId, Waypoint } from '@/lib/types'
import { decodeRouteFromUrl } from '@/lib/url-state'
import { getVehicle } from '@/lib/vehicles'
import { loadSavedAuxPower, useRouteStore } from '@/stores/route-store'
import { AboutSection } from './about-section'
import { AuxPowerInput } from './aux-power-input'
import { CalculationSkeleton } from './calculation-skeleton'
import { CalculationWarnings } from './calculation-warnings'
import { ClearanceInput } from './clearance-input'
import { CostSummary } from './cost-summary'
import { CustomMpgInput } from './custom-mpg-input'
import { CustomTankInput } from './custom-tank-input'
import { ElevationChart } from './elevation-chart'
import { EstimatorHeader } from './estimator-header'
import { FuelPlan } from './fuel-plan'
import { FuelTypeToggle } from './fuel-type-toggle'
import { HotStateChip } from './hot-state-chip'
import { LocationSearch } from './location-search'
import { MapErrorBoundary } from './map-error-boundary'
import { RecentRoutes } from './recent-routes'
import RouteImportButton from './route-import/RouteImportButton'
import { StateBreakdown } from './state-breakdown'
import { VehiclePicker } from './vehicle-picker'

/**
 * Mapbox GL JS is ~220 KB gzipped — by far our largest single dependency,
 * and we don't need it until the browser is idle and ready to render the
 * map. Dynamic-importing with `ssr: false` keeps it out of the initial
 * bundle entirely:
 *
 *   - First paint is faster (Lighthouse "Reduce unused JavaScript" was
 *     flagging ~373 KiB, Mapbox was the lion's share).
 *   - The form, ticker, footer, and CalculateButton all render + respond
 *     to keystrokes before the map is loaded. On slow connections this
 *     is the difference between "usable in 1s" and "usable in 4s."
 *   - Skeleton fills the map area during load so layout doesn't shift
 *     when the real component resolves.
 *
 * ssr: false is safe here because the map reads the mapbox-gl CSS and
 * window globals at import time — it can't render on the server anyway.
 */
const RouteMap = dynamic(() => import('./route-map').then((m) => ({ default: m.RouteMap })), {
	ssr: false,
	loading: () => <Skeleton className="h-full min-h-[300px] w-full rounded-lg" />,
})

/** Load route state from URL search params and trigger calculation */
function loadRouteFromUrl(searchParams: URLSearchParams) {
	if (!searchParams.get('from')) return
	const state = decodeRouteFromUrl(searchParams)
	const store = useRouteStore.getState()
	// Settings go in BEFORE any stop: with no origin/destination yet, their
	// route-preview refreshes are no-ops. Stops land last in ONE importStops,
	// so opening a link costs one billed Directions preview — not one per
	// setter and waypoint (a 10-stop link used to fire 14 and trip the
	// 30/min Directions limit before Calculate).
	// Vehicle MUST be set before the shared settings — setVehicle() restores
	// the recipient's own saved overrides, which the link then replaces.
	if (state.vehicle) store.setVehicle(state.vehicle)
	if (state.fuelType) store.setFuelType(state.fuelType)
	// The link fully defines the estimate (absent = preset / off), applied for
	// this session only — opening someone's link never overwrites your saved
	// MPG, height, or generator settings.
	store.applySharedSettings({
		customMpg: state.customMpg,
		customHeightM: state.customHeightM,
		auxPowerOn: state.auxPowerOn,
		customGph: state.customGph,
		auxIdleHours: state.auxIdleHours,
	})
	if (state.from && state.to) {
		store.importStops([state.from, ...state.waypoints, state.to])
		setTimeout(() => useRouteStore.getState().calculate(), 100)
	} else if (state.from) {
		store.setOrigin(state.from)
	}
}

/**
 * Smart Fuel Plan — SHELVED 2026-04-19 pending UX rethink.
 *
 * The feature works algorithmically (greedy gas-station-problem optimizer
 * with state-zone pricing — see fuel-stops.ts + fuel-stops.test.ts, 11/11
 * tests passing). But the displayed totals create a confusing read:
 *
 *   - State Breakdown total = total trip fuel cost valued at per-state
 *     prices (~$1,200 on a Portland → Denver Prevost run)
 *   - Plan total = pump receipts during the trip (~$400 on the same run)
 *
 * Both are correct individually but they measure different things, and
 * stacking them on one screen makes them look like contradictory claims.
 * The "refueling on the fly" comparison also overstates savings on routes
 * where every stop happens at the same PADD price (the difference is
 * mostly because the simulated naive driver overfills at the last stop,
 * leaving more residual fuel — that residual carries over to the next
 * trip so the "savings" is partly phantom).
 *
 * To re-enable end-to-end: flip `FUEL_PLAN_ENABLED` (in @/lib/flags) to
 * true. All code is preserved — tank-capacity inputs, optimizer, FuelPlan
 * panel, numbered map markers, vehicle-card capacity column. When picking
 * it back up, revisit:
 *
 *   1. How "Plan total" relates conceptually to the State Breakdown total
 *      (consider pricing the starting tank in, or relabeling explicitly)
 *   2. The naive-driver simulation's overfill bias
 *   3. Whether to surface starting-fuel-level as an explicit user input
 *   4. Two known optimizer bugs flagged by Codex 2026-04-19 (notes inline
 *      in fuel-stops.ts at the affected functions):
 *      - checkFeasibility() incorrectly rejects long single-state routes
 *        even though multiple in-state fills at the same average price
 *        are valid in our state-level pricing model
 *      - MIN_FILL_GALLONS silently drops *required* small refuels, leaving
 *        the planner reporting $0 spend for trips that physically need fuel
 */

/**
 * Compute the fuel plan + effective tank capacity for the active estimate.
 *
 * Extracted so `FuelEstimatorInner` stays under the cognitive-complexity
 * ceiling — the `??` and ternary here together count as 2 complexity points
 * that would otherwise live inline in the main component. Pure helper,
 * no hooks of its own; called like a hook for naming consistency.
 */
function useFuelPlan(
	estimate: ReturnType<typeof useRouteStore.getState>['estimate'],
	vehicleId: VehicleId,
	customTankCapacity: number | null,
): { fuelPlan: ReturnType<typeof planFuelStops> | null; tankCapacityGallons: number } {
	const tankCapacityGallons = customTankCapacity ?? getVehicle(vehicleId).tankCapacityGallons
	const fuelPlan = estimate ? planFuelStops(estimate.stateBreakdown, tankCapacityGallons) : null
	return { fuelPlan, tankCapacityGallons }
}

/**
 * Tank-capacity input — gated by FUEL_PLAN_ENABLED. Wrapped in its own
 * component so the parent's cognitive complexity stays under the ceiling
 * while the feature is shelved. When the flag flips on, this becomes a
 * direct passthrough to CustomTankInput with zero parent-side changes.
 */
function ShelvedTankInput(props: {
	vehicleId: VehicleId
	value: number | null
	onChange: (gallons: number | null) => void
}) {
	if (!FUEL_PLAN_ENABLED) return null
	return <CustomTankInput {...props} />
}

/**
 * Fuel plan panel — gated by FUEL_PLAN_ENABLED. Same pattern as the tank
 * input wrapper above: keeps the gating logic out of the parent's
 * complexity budget while preserving the full call site for re-enable.
 */
function ShelvedFuelPlan(props: {
	fuelPlan: ReturnType<typeof planFuelStops> | null
	tankCapacityGallons: number
}) {
	if (!FUEL_PLAN_ENABLED || !props.fuelPlan) return null
	return <FuelPlan plan={props.fuelPlan} tankCapacityGallons={props.tankCapacityGallons} />
}

/**
 * Map-marker props — also gated. Returns undefined for both fuel-related
 * props when the feature is shelved, which RouteMap already handles
 * gracefully (renders the route line without any fuel-stop markers).
 */
function shelvedMapFuelProps(
	fuelPlan: ReturnType<typeof planFuelStops> | null,
	estimate: ReturnType<typeof useRouteStore.getState>['estimate'],
): {
	fuelStops: NonNullable<ReturnType<typeof planFuelStops>>['stops'] | undefined
	elevationProfile: NonNullable<typeof estimate>['elevationProfile'] | undefined
} {
	if (!FUEL_PLAN_ENABLED) return { fuelStops: undefined, elevationProfile: undefined }
	return {
		fuelStops: fuelPlan?.stops,
		elevationProfile: estimate?.elevationProfile,
	}
}

function FuelEstimatorInner() {
	const searchParams = useSearchParams()
	const {
		origin,
		destination,
		waypoints,
		vehicleId,
		fuelType,
		customMpg,
		customTankCapacity,
		customHeightM,
		auxPowerOn,
		customGph,
		auxIdleHours,
		estimate,
		routeGeometry,
		isCalculating,
		error,
		lastCalculationMs,
		wasCached,
		setOrigin,
		setDestination,
		addWaypoint,
		removeWaypoint,
		updateWaypoint,
		setVehicle,
		setFuelType,
		setCustomMpg,
		setCustomTankCapacity,
		setCustomHeightM,
		setAuxPowerOn,
		setCustomGph,
		setAuxIdleHours,
		importStops,
		calculate,
		reset,
	} = useRouteStore()

	// Track empty stop fields that haven't been resolved to a location yet
	const nextKeyRef = useRef(0)
	const [pendingStops, setPendingStops] = useState<number[]>([])

	const totalStops = waypoints.length + pendingStops.length
	const canAddStop = origin && destination && totalStops < MAX_WAYPOINTS

	const handleAddStop = useCallback(() => {
		setPendingStops((prev) => [...prev, nextKeyRef.current++])
	}, [])

	const handlePendingSelect = useCallback(
		(slotKey: number, wp: Waypoint | null) => {
			if (!wp) return
			addWaypoint(wp)
			setPendingStops((prev) => prev.filter((k) => k !== slotKey))
		},
		[addWaypoint],
	)

	/**
	 * Load a recent route into the form and trigger Calculate. REPLACES the
	 * whole route in one atomic `importStops` — appending the recent route's
	 * stops via `addWaypoint` used to keep any waypoints already on the form
	 * (e.g. after clearing just the endpoints). Any empty pending stop rows go
	 * too: they belonged to the route being replaced.
	 */
	const handlePickRecent = useCallback(
		(r: import('@/lib/recent-routes').RecentRoute) => {
			importStops([r.origin, ...r.waypoints, r.destination])
			setPendingStops([])
			calculate()
		},
		[importStops, calculate],
	)

	/**
	 * Reset-all-inputs callback. Lifted out of JSX so FuelEstimatorInner
	 * stays under the cognitive-complexity ceiling. The header component
	 * receives this via props and calls it when the user clicks Reset.
	 */
	const handleReset = useCallback(() => {
		reset()
		setPendingStops([])
	}, [reset])

	const handleRemovePending = useCallback((slotKey: number) => {
		setPendingStops((prev) => prev.filter((k) => k !== slotKey))
	}, [])

	// Load state from URL on mount
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on mount
	useEffect(() => {
		loadRouteFromUrl(searchParams)
	}, [])

	// Hydrate customMpg from localStorage AFTER mount (client-only). The
	// store initializes customMpg = null to keep SSR and client hydration
	// consistent — localStorage doesn't exist on the server, so if we read
	// it during store construction React trips on the mismatch.
	//
	// Ordering vs loadRouteFromUrl (Codex follow-up 2026-04-19, High #1):
	// Both mount effects have empty deps, so they run in declared order
	// after first commit — loadRouteFromUrl first, then this hydration.
	// Previous version captured `vehicleId` in the closure at render time,
	// which was still the store's initial 'nightliner' default even after
	// loadRouteFromUrl had already called setVehicle('sedan'). That read
	// the WRONG localStorage key and could overwrite the URL's customMpg
	// with a value saved for a completely different vehicle.
	//
	// Fix: read fresh from useRouteStore.getState() so we see the CURRENT
	// vehicleId (after any URL-driven setVehicle). Also skip entirely if
	// customMpg is already set — if the URL already loaded one, it's
	// authoritative and localStorage must not clobber it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only hydration
	useEffect(() => {
		if (typeof window === 'undefined') return
		const current = useRouteStore.getState()
		if (current.fromShareLink) return
		// URL state (or any other path) has already populated customMpg —
		// don't overwrite.
		if (current.customMpg !== null) return
		try {
			const saved = window.localStorage.getItem(storageKeys.customMpg(current.vehicleId))
			const parsed = saved === null ? null : Number.parseFloat(saved)
			if (parsed !== null && Number.isFinite(parsed) && parsed >= 1 && parsed <= 50) {
				setCustomMpg(parsed)
			}
		} catch {
			// localStorage may be unavailable (private mode, quota) — skip silently
		}
	}, [])

	// Same SSR-safe hydration pattern for customTankCapacity. Same reasoning
	// applies — the store starts null on both server and client to avoid the
	// hydration mismatch, then we read localStorage after mount. See the
	// customMpg hydration above for the full rationale.
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only hydration
	useEffect(() => {
		if (typeof window === 'undefined') return
		const current = useRouteStore.getState()
		if (current.fromShareLink) return
		if (current.customTankCapacity !== null) return
		try {
			const saved = window.localStorage.getItem(storageKeys.customTank(current.vehicleId))
			const parsed = saved === null ? null : Number.parseFloat(saved)
			if (parsed !== null && Number.isFinite(parsed) && parsed >= 5 && parsed <= 500) {
				setCustomTankCapacity(parsed)
			}
		} catch {
			// localStorage may be unavailable — skip silently
		}
	}, [])

	// Same SSR-safe hydration for the vehicle-height override (bridge-aware
	// routing). Bounds match the directions API's accepted range.
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only hydration
	useEffect(() => {
		if (typeof window === 'undefined') return
		const current = useRouteStore.getState()
		if (current.fromShareLink) return
		if (current.customHeightM !== null) return
		try {
			const saved = window.localStorage.getItem(storageKeys.customHeight(current.vehicleId))
			const parsed = saved === null ? null : Number.parseFloat(saved)
			if (
				parsed !== null &&
				Number.isFinite(parsed) &&
				parsed >= HEIGHT_MIN_M &&
				parsed <= HEIGHT_MAX_M
			) {
				setCustomHeightM(parsed)
			}
		} catch {
			// localStorage may be unavailable — skip silently
		}
	}, [])

	// Generator/reefer toggle + custom rate, per vehicle (SSR-safe, as above).
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only hydration
	useEffect(() => {
		const current = useRouteStore.getState()
		if (current.fromShareLink) return
		const saved = loadSavedAuxPower(current.vehicleId)
		if (saved.on) setAuxPowerOn(true)
		if (current.customGph === null && saved.gph !== null) setCustomGph(saved.gph)
	}, [])

	// Warm the EIA price cache on mount. By the time the user picks a route and
	// clicks Calculate (~10s minimum of interaction), the server's 24h cache is
	// populated and the pipeline's fuel-prices call returns in <100ms. Fires
	// once per visit — fire-and-forget, errors are silent.
	useEffect(() => {
		fetch('/api/fuel-prices').catch(() => {})
	}, [])

	const canCalculate = origin && destination && !isCalculating

	// Fuel plan + effective tank capacity for the active estimate. Computed
	// in a small helper hook so the parent's cognitive complexity stays under
	// the project's 15-cap. Both FuelPlan (panel) and RouteMap (markers)
	// consume the same plan instance.
	const { fuelPlan, tankCapacityGallons } = useFuelPlan(estimate, vehicleId, customTankCapacity)

	/**
	 * Enter-key anywhere in the form triggers Calculate when origin + destination
	 * are both set. LocationSearch stops propagation on Enter when its dropdown
	 * is open (so selecting an autocomplete result doesn't accidentally fire
	 * Calculate). Other inputs (custom MPG number field) bubble up normally.
	 */
	const handleFormKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && canCalculate && !e.defaultPrevented) {
			e.preventDefault()
			calculate()
		}
	}

	return (
		<div className="flex min-h-screen flex-col">
			{/* The page's single <h1> — keyword-rich for SEO/AEO. Rendered sr-only
			    because the visual brand mark is the logo; establishes the heading
			    hierarchy (h1 → hero/section h2s → FAQ h3s). */}
			<h1 className="sr-only">
				Fuel Estimator — tour bus &amp; truck fuel cost calculator with elevation-adjusted MPG
			</h1>
			<EstimatorHeader
				origin={origin}
				destination={destination}
				waypoints={waypoints}
				vehicleId={vehicleId}
				fuelType={fuelType}
				customMpg={customMpg}
				estimate={estimate}
				onReset={handleReset}
			/>

			{/* Guide strip — bridges the hero and the form. A light divider plus a
			    one-line nudge for first-time visitors; also fills the gap above the
			    form on wide screens. */}
			<div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
				<div className="flex flex-col items-center gap-1.5 border-t border-border py-5 text-center sm:py-6">
					<p className="text-sm text-text-primary sm:text-base">
						Enter your route details below and select the preset that best suits your vehicle.
					</p>
					<p className="text-xs text-text-secondary sm:text-sm">
						We handle the elevation-adjusted MPG and live per-state fuel pricing for you.
					</p>
				</div>
			</div>

			{/* Main content — <main> landmark for screen reader + keyboard skip navigation */}
			<main
				id="main"
				onKeyDown={handleFormKeyDown}
				className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-6 px-4 pb-6 pt-2 sm:px-6 lg:flex-row"
			>
				{/* Left column — form + results */}
				<div className="flex w-full flex-col gap-4 lg:w-[380px] lg:shrink-0">
					{/* Recent routes chip row — only visible when the form is empty so
					    returning users see their last 5 trips and can re-run one in a
					    single click. Hides the moment they start typing a new route
					    so it never competes with the active form. */}
					{!origin && !destination && (
						<>
							<HotStateChip />
							<RecentRoutes onPick={handlePickRecent} />
						</>
					)}

					{/* Route inputs */}
					<div className="space-y-3">
						<RouteImportButton />

						<LocationSearch
							label="Origin"
							value={origin}
							onChange={setOrigin}
							placeholder="Where are you starting?"
						/>

						{/* Resolved waypoints */}
						{waypoints.map((wp, i) => (
							<div key={`wp-${wp.lat}-${wp.lng}`} className="relative">
								<LocationSearch
									label={`Stop ${i + 1}`}
									value={wp}
									onChange={(newWp) => {
										if (newWp) {
											updateWaypoint(i, newWp)
										} else {
											removeWaypoint(i)
										}
									}}
								/>
								<button
									type="button"
									onClick={() => removeWaypoint(i)}
									aria-label={`Remove stop ${i + 1}`}
									className="absolute right-0 top-0 p-1 text-text-muted hover:text-error"
								>
									<X className="size-3" />
								</button>
							</div>
						))}

						{/* Pending (empty) stop fields */}
						{pendingStops.map((slotKey) => (
							<div key={`pending-${slotKey}`} className="relative">
								<LocationSearch
									label={`Stop ${waypoints.length + pendingStops.indexOf(slotKey) + 1}`}
									value={null}
									onChange={(wp) => handlePendingSelect(slotKey, wp)}
									placeholder="Search for a stop..."
								/>
								<button
									type="button"
									onClick={() => handleRemovePending(slotKey)}
									aria-label="Remove empty stop"
									className="absolute right-0 top-0 p-1 text-text-muted hover:text-error"
								>
									<X className="size-3" />
								</button>
							</div>
						))}

						<LocationSearch
							label="Destination"
							value={destination}
							onChange={setDestination}
							placeholder="Where are you going?"
						/>

						{canAddStop && (
							<Button
								variant="ghost"
								size="xs"
								onClick={handleAddStop}
								className="w-full text-text-muted"
							>
								<Plus className="size-3" />
								Add stop{totalStops > 0 ? ` (${totalStops}/${MAX_WAYPOINTS})` : ''}
							</Button>
						)}
					</div>

					<Separator />

					{/* Vehicle + Fuel */}
					<VehiclePicker value={vehicleId} onChange={setVehicle} customMpg={customMpg} />
					<CustomMpgInput vehicleId={vehicleId} value={customMpg} onChange={setCustomMpg} />
					<ClearanceInput vehicleId={vehicleId} value={customHeightM} onChange={setCustomHeightM} />
					<AuxPowerInput
						vehicleId={vehicleId}
						on={auxPowerOn}
						onToggle={setAuxPowerOn}
						gph={customGph}
						onGphChange={setCustomGph}
						idleHours={auxIdleHours}
						onIdleChange={setAuxIdleHours}
					/>
					<ShelvedTankInput
						vehicleId={vehicleId}
						value={customTankCapacity}
						onChange={setCustomTankCapacity}
					/>
					<FuelTypeToggle value={fuelType} onChange={setFuelType} />

					<Separator />

					{/* Calculate button */}
					<Button onClick={calculate} disabled={!canCalculate} className="w-full" size="lg">
						{isCalculating ? (
							<>
								<Loader2 className="size-4 animate-spin" />
								Calculating...
							</>
						) : (
							<>
								<Fuel className="size-4" />
								Estimate Fuel Cost
							</>
						)}
					</Button>

					{error && (
						<p className="text-sm text-error-fg" role="alert" aria-live="assertive">
							{error}
						</p>
					)}

					{/* Results
					   Priority: skeleton while actively calculating (hides any stale
					   prior estimate so it doesn't flash old numbers), then real
					   results once the pipeline resolves. No estimate + not
					   calculating = nothing to render. */}
					{isCalculating ? (
						<CalculationSkeleton />
					) : (
						estimate && (
							<div className="space-y-4">
								<CalculationWarnings warnings={estimate.warnings} />
								<CostSummary
									estimate={estimate}
									calculationMs={lastCalculationMs}
									wasCached={wasCached}
								/>
								<ElevationChart profile={estimate.elevationProfile} />
								<StateBreakdown segments={estimate.stateBreakdown} fuelType={estimate.fuelType} />
								<ShelvedFuelPlan fuelPlan={fuelPlan} tankCapacityGallons={tankCapacityGallons} />
							</div>
						)
					)}
				</div>

				{/* Right column — map. Wrapped in MapErrorBoundary so a WebGL
				    context loss, bad Mapbox tile, or any Mapbox GL JS runtime
				    error shows a graceful "Map unavailable" card instead of
				    crashing the whole app. Costs/share/PDF keep working.
				    resetKey changes whenever the user starts a new calculation,
				    so a fresh attempt auto-retries the map mount. */}
				<div className="flex-1 overflow-hidden rounded-lg border border-border min-h-[250px] sm:min-h-[350px] lg:min-h-[500px]">
					<MapErrorBoundary
						resetKey={`${origin?.lng ?? ''},${origin?.lat ?? ''}→${destination?.lng ?? ''},${destination?.lat ?? ''}`}
					>
						<RouteMap
							origin={origin}
							destination={destination}
							waypoints={waypoints}
							routeGeometry={routeGeometry}
							{...shelvedMapFuelProps(fuelPlan, estimate)}
						/>
					</MapErrorBoundary>
				</div>
			</main>

			{/* Long-form SEO/AEO content + visible FAQ (matches the FAQPage JSON-LD) */}
			<AboutSection />

			{/* Footer */}
			<footer className="border-t border-border bg-surface px-4 py-4 sm:py-5 sm:px-6 safe-bottom">
				<div className="mx-auto max-w-7xl flex flex-col gap-3 text-center text-xs text-text-muted sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:text-left">
					{/* Left: provenance — a free personal give-back project. */}
					<p>
						A free personal project by Chris Lee Bergstrom · Elevation-adjusted MPG for accurate
						touring estimates
					</p>

					{/* Right: data sources + policy links. Dotted-underline keeps the
					    footer visually tidy while still marking each item as a link
					    on first sight (passes WCAG 1.4.1). */}
					<nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px]">
						<span>
							Prices:{' '}
							<a
								href="https://www.eia.gov/petroleum/gasdiesel/"
								className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
								target="_blank"
								rel="noopener noreferrer"
							>
								EIA
							</a>
						</span>
						<span aria-hidden>·</span>
						<span>
							Elevation:{' '}
							<a
								href="https://open-meteo.com/"
								className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
								target="_blank"
								rel="noopener noreferrer"
							>
								Open-Meteo
							</a>
						</span>
						<span aria-hidden>·</span>
						<span>
							Maps:{' '}
							<a
								href="https://www.mapbox.com/"
								className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
								target="_blank"
								rel="noopener noreferrer"
							>
								Mapbox
							</a>
						</span>
						<span aria-hidden>·</span>
						<a
							href="/privacy"
							className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
						>
							Privacy
						</a>
						<span aria-hidden>·</span>
						<a
							href="/terms"
							className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
						>
							Terms
						</a>
						{process.env.NEXT_PUBLIC_BUILD_SHA && (
							<>
								<span aria-hidden>·</span>
								<span className="font-mono text-text-muted" title="Build identifier">
									v.{process.env.NEXT_PUBLIC_BUILD_SHA}
								</span>
							</>
						)}
					</nav>
				</div>
			</footer>
		</div>
	)
}

export function FuelEstimator() {
	return (
		<Suspense
			fallback={
				<div className="flex min-h-screen items-center justify-center">
					<Skeleton className="h-8 w-48" />
				</div>
			}
		>
			<FuelEstimatorInner />
		</Suspense>
	)
}
